import React from 'react';
import type {
  ColDef,
  EditableCallbackParams,
  ValueFormatterParams,
} from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import {
  getCreationRedemptions,
  saveCreationRedemptionsBatch,
} from '../../api/creationRedemptions';
import { useFunds } from '../../hooks/useFunds';
import type { CreationRedemption } from '../../types/CreationRedemption';
import { t } from '../../i18n/t';

// "יצירות ופדיונות" — per-trade creation/redemption activity for fund units,
// reported by exchange members. TENANT-scoped: gate rendering on useFunds()
// (which self-sufficiently calls setTenantId) before the first fetch to
// avoid the hard-refresh X-Tenant-Id race — mirrors CapitalConsolidationsPage.
// recId is a client-supplied natural PK, editable only while the row is new
// (mirrors AccountPurposesPage's editableOnAdd rule). tradeTime is a
// read-only DATETIME, formatted like PublishedFundRatesPage's capturedAt /
// AuditLogPage's loggedAt.

function fmtDateTime(value: unknown): string {
  if (!value) return '';
  const s = String(value);
  return s.length >= 19 ? s.slice(0, 19).replace('T', ' ') : s;
}

const isAddedRow = (params: EditableCallbackParams<CreationRedemption>) =>
  (params.data as { __status?: string } | undefined)?.__status === 'added';

const columnDefs: ColDef<CreationRedemption>[] = [
  {
    field: 'recId',
    headerName: t('creationRedemptions.columns.recId'),
    width: 100,
    editable: isAddedRow,
    cellDataType: 'number',
    sort: 'asc',
  },
  {
    field: 'fundAssetId',
    headerName: t('creationRedemptions.columns.fundAssetId'),
    width: 130,
    editable: true,
    cellDataType: 'number',
  },
  {
    field: 'securityShortName',
    headerName: t('creationRedemptions.columns.securityShortName'),
    width: 160,
    editable: true,
  },
  {
    field: 'buyUnits',
    headerName: t('creationRedemptions.columns.buyUnits'),
    width: 130,
    editable: true,
    cellDataType: 'number',
  },
  {
    field: 'buyValue',
    headerName: t('creationRedemptions.columns.buyValue'),
    width: 130,
    editable: true,
    cellDataType: 'number',
  },
  {
    field: 'sellUnits',
    headerName: t('creationRedemptions.columns.sellUnits'),
    width: 130,
    editable: true,
    cellDataType: 'number',
  },
  {
    field: 'sellValue',
    headerName: t('creationRedemptions.columns.sellValue'),
    width: 130,
    editable: true,
    cellDataType: 'number',
  },
  {
    field: 'totalUnits',
    headerName: t('creationRedemptions.columns.totalUnits'),
    width: 130,
    editable: true,
    cellDataType: 'number',
  },
  {
    field: 'totalValue',
    headerName: t('creationRedemptions.columns.totalValue'),
    width: 130,
    editable: true,
    cellDataType: 'number',
  },
  {
    field: 'creationPrice',
    headerName: t('creationRedemptions.columns.creationPrice'),
    width: 130,
    editable: true,
    cellDataType: 'number',
  },
  {
    field: 'redemptionPrice',
    headerName: t('creationRedemptions.columns.redemptionPrice'),
    width: 130,
    editable: true,
    cellDataType: 'number',
  },
  {
    field: 'memberName',
    headerName: t('creationRedemptions.columns.memberName'),
    width: 160,
    editable: true,
  },
  {
    field: 'memberNumber',
    headerName: t('creationRedemptions.columns.memberNumber'),
    width: 120,
    editable: true,
    cellDataType: 'number',
  },
  {
    field: 'tradeStage',
    headerName: t('creationRedemptions.columns.tradeStage'),
    width: 130,
    editable: true,
  },
  {
    field: 'tradeTime',
    headerName: t('creationRedemptions.columns.tradeTime'),
    width: 160,
    editable: false,
    valueFormatter: (p: ValueFormatterParams<CreationRedemption>) =>
      fmtDateTime(p.value),
  },
  {
    field: 'crcyId',
    headerName: t('creationRedemptions.columns.crcyId'),
    width: 100,
    editable: true,
    cellDataType: 'number',
  },
];

const newCreationRedemptionDefaults = (): Partial<CreationRedemption> => ({
  recId: 0,
  fundAssetId: 0,
  securityShortName: null,
  buyUnits: null,
  buyValue: null,
  sellUnits: null,
  sellValue: null,
  totalUnits: null,
  totalValue: null,
  creationPrice: null,
  redemptionPrice: null,
  memberName: null,
  memberNumber: null,
  tradeStage: null,
  tradeTime: null,
  crcyId: null,
});

export function CreationRedemptionsPage() {
  // Tenant-scoped gate — see module comment. Only used for the setTenantId
  // side effect / loading gate here (no FK dropdown needs the fund list).
  const { loading: fundsLoading, error: fundsError } = useFunds();

  if (fundsError) {
    return (
      <div style={{ padding: 16 }}>
        <div
          style={{
            color: 'var(--color-error-text)',
            background: 'var(--color-error-bg)',
            padding: '8px 12px',
            borderRadius: 4,
          }}
        >
          {t('creationRedemptions.errorPrefix', { message: fundsError })}
        </div>
      </div>
    );
  }

  if (fundsLoading) {
    return (
      <div style={{ padding: 16, color: 'var(--color-text-secondary)' }}>{t('common.loading')}</div>
    );
  }

  return (
    <LopCrudPage<CreationRedemption>
      resourceCode="creationRedemptions"
      titleKey="creationRedemptions.pageTitle"
      idField="recId"
      getRows={getCreationRedemptions}
      saveBatch={(added, modified, deletedIds) =>
        saveCreationRedemptionsBatch(added, modified, deletedIds as number[])
      }
      columnDefs={columnDefs}
      newRowDefaults={newCreationRedemptionDefaults}
    />
  );
}
